import { useState, type ReactNode } from 'react';
import {
  Mail,
  MailCheck,
  MoreHorizontal,
  Pencil,
  ShieldCheck,
  ShieldOff,
  Trash2,
} from 'lucide-react';

import type { AdminUserListItem } from '../../../api/adminUsers';
import { Button } from '../../../components/ui/button';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  useDeleteAdminUser,
  useForceAdminUserEmailVerified,
  useResendAdminInvite,
  useToggleAdminUserBlocked,
} from '../../../hooks/admin/useAdminUsers';

interface UserActionsMenuProps {
  user: AdminUserListItem;
  isSelf: boolean;
  onEdit: () => void;
}

type Confirm = 'block' | 'unblock' | 'delete' | 'forceEmailVerified' | null;

export function UserActionsMenu({ user, isSelf, onEdit }: UserActionsMenuProps) {
  const [confirm, setConfirm] = useState<Confirm>(null);

  const toggleBlocked = useToggleAdminUserBlocked(user.id);
  const resendInvite = useResendAdminInvite(user.id);
  const deleteUser = useDeleteAdminUser(user.id);
  const forceEmailVerified = useForceAdminUserEmailVerified(user.id);

  const closeConfirm = () => setConfirm(null);

  const handleConfirm = () => {
    if (confirm === 'block') toggleBlocked.mutate(true, { onSuccess: closeConfirm });
    if (confirm === 'unblock') toggleBlocked.mutate(false, { onSuccess: closeConfirm });
    if (confirm === 'delete') deleteUser.mutate(undefined, { onSuccess: closeConfirm });
    if (confirm === 'forceEmailVerified')
      forceEmailVerified.mutate(undefined, { onSuccess: closeConfirm });
  };

  const confirmConfig: Record<
    Exclude<Confirm, null>,
    {
      title: string;
      message: ReactNode;
      confirmLabel: string;
      confirmColor: 'primary' | 'error' | 'warning';
    }
  > = {
    block: {
      title: 'Bloquear usuário?',
      message: `${user.nome} (${user.email}) não conseguirá mais fazer login até ser desbloqueado.`,
      confirmLabel: 'Bloquear',
      confirmColor: 'warning' as const,
    },
    unblock: {
      title: 'Desbloquear usuário?',
      message: `${user.nome} voltará a poder fazer login normalmente.`,
      confirmLabel: 'Desbloquear',
      confirmColor: 'primary' as const,
    },
    delete: {
      title: 'Excluir usuário?',
      message: `Esta ação removerá ${user.nome} do Auth0 e desativará o registro local. Não pode ser desfeita.`,
      confirmLabel: 'Excluir',
      confirmColor: 'error' as const,
    },
    forceEmailVerified: {
      title: 'Forçar verificação de e-mail',
      message: (
        <>
          Marca o e-mail de <strong>{user.email}</strong> como verificado no Auth0
          e localmente, sem que o usuário precise clicar no link de verificação.
          Use apenas quando o e-mail não estiver chegando. Esta ação é registrada
          na auditoria.
        </>
      ),
      confirmLabel: 'Forçar verificação',
      confirmColor: 'primary' as const,
    },
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={`Ações para ${user.nome}`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem onSelect={onEdit}>
            <Pencil />
            Editar
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => resendInvite.mutate()}
            disabled={!user.auth0_id || resendInvite.isPending}
          >
            <Mail />
            Reenviar convite
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setConfirm('forceEmailVerified')}
            disabled={!user.auth0_id || user.email_verified || forceEmailVerified.isPending}
          >
            <MailCheck />
            Forçar verificação de e-mail
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {user.is_active ? (
            <DropdownMenuItem onSelect={() => setConfirm('block')} disabled={isSelf}>
              <ShieldOff />
              Bloquear
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={() => setConfirm('unblock')}>
              <ShieldCheck />
              Desbloquear
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onSelect={() => setConfirm('delete')}
            disabled={isSelf}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 />
            Excluir
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {confirm && (
        <ConfirmDialog
          open
          title={confirmConfig[confirm].title}
          message={confirmConfig[confirm].message}
          confirmLabel={confirmConfig[confirm].confirmLabel}
          confirmColor={confirmConfig[confirm].confirmColor}
          loading={
            toggleBlocked.isPending || deleteUser.isPending || forceEmailVerified.isPending
          }
          onConfirm={handleConfirm}
          onCancel={closeConfirm}
        />
      )}
    </>
  );
}

export default UserActionsMenu;
